import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { captureLandingCampaign, trackEvent, trackHomepageAction } from "@/lib/analytics";
import heroImg from "@assets/Black_African_business_owner_using_a_modern_POS_terminal_in_a__1779730872541.jpg";
import featureCloudImg from "@assets/African_business_owner_reviewing_secure_cloud_dashboard,_text__1779730919350.jpg";
import featureReceiptImg from "@assets/African_cashier_handing_customer_a_receipt_with_discount_code,_1779730919364.jpg";
import featureMobileImg from "@assets/African_entrepreneur_using_smartphone_showing_POS_dashboard,_t_1779730919366.jpg";
import featureJoinImg from "@assets/African_retail_business_owner_smiling_confidently,_text_'Join__1779730919388.jpg";
import holidayImg1 from "@assets/Festive_Nigerian_retail_shop_decorated_for_Christmas,_POS_term_1779731005134.jpg";
import holidayImg2 from "@assets/Festive_Nigerian_retail_shop_decorated_for_Christmas,_POS_term_1779731005138.jpg";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  ShoppingCart,
  Users,
  BarChart3,
  Landmark,
  CheckCircle2,
  Star,
  ArrowRight,
  Store,
  TrendingUp,
  Shield,
  Zap,
  Clock,
  RefreshCcw,
  Mail,
  Phone,
  MapPin,
  ChevronDown,
  ChevronUp,
  Twitter,
  Linkedin,
  Instagram,
  Facebook,
  MessageCircle,
  Crown,
  Truck,
  Globe,
  Wrench,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

// ─── Data ──────────────────────────────────────────────────────────────────

const WHATSAPP_URL = "https://wa.me/2349040460813";

const PLANS = [
  {
    name: "Basic",
    price: "₦7,500",
    period: "/month",
    description: "Perfect for small retailers just getting started.",
    features: [
      "Up to 1,500 products",
      "Full POS & sales management",
      "Inventory tracking & low-stock alerts",
      "Supplier & purchase orders",
      "Up to 3 staff accounts",
      "Expense tracking",
      "Sales history & receipts",
      "100 Bulk SMS credits/month",
      "10-day free trial",
    ],
    cta: "Start Free Trial",
    highlight: false,
    enterprise: false,
  },
  {
    name: "Pro",
    price: "₦12,000",
    period: "/month",
    description: "Advanced analytics and reporting for growing stores.",
    features: [
      "Up to 2,500 products",
      "Full POS & sales management",
      "Inventory tracking & low-stock alerts",
      "Supplier & purchase orders",
      "Up to 7 staff accounts",
      "Expense tracking",
      "Expense Reports & Analytics",
      "Payment Reports",
      "Profit & Loss Statement",
      "Cash Flow Statement",
      "Staff Performance Reports",
      "Supplier Performance Reports",
      "Customer Reports",
      "Multi-Branch Management",
      "200 Bulk SMS credits/month",
    ],
    cta: "Start Free Trial",
    highlight: true,
    enterprise: false,
  },
  {
    name: "Enterprise",
    price: "₦28,000",
    period: "/month",
    description: "Full suite with premium features for large operations.",
    features: [
      "Unlimited products",
      "Full POS & sales management",
      "Inventory tracking & low-stock alerts",
      "Supplier & purchase orders",
      "Full Accounting Suite (P&L, Cash Flow)",
      "Balance Sheet",
      "Loyalty & Discount Management",
      "Staff Clock-in / Clock-out",
      "Checklists & Operations",
      "All Advanced Reports",
      "Unlimited staff accounts",
      "Multi-Branch Management",
      "Gift Voucher Management",
      "500 Bulk SMS credits/month",
      "Dedicated priority support",
    ],
    cta: "Subscribe",
    highlight: false,
    enterprise: true,
  },
];

const FEATURES = [
  {
    icon: ShoppingCart,
    title: "Point of Sale",
    description:
      "Fast, intuitive POS built for Nigerian retail. Process sales, apply discounts, accept cash/card/transfer, and print receipts instantly.",
  },
  {
    icon: Package,
    title: "Inventory Management",
    description:
      "Track stock levels in real-time. Set reorder points, get low-stock alerts, and never run out of your best-selling products.",
  },
  {
    icon: Users,
    title: "Staff & Customers",
    description:
      "Manage your team with role-based access, track performance, and build customer loyalty with points and discount rates.",
  },
  {
    icon: Landmark,
    title: "Full Accounting",
    description:
      "Expenses, payments, P&L, balance sheet, and cash flow — a complete back-office accounting suite in one dashboard.",
  },
  {
    icon: BarChart3,
    title: "Reports & Analytics",
    description:
      "Understand your business with detailed sales reports, revenue trends, category breakdowns, and profit analysis.",
  },
  {
    icon: TruckIcon,
    title: "Purchases & Suppliers",
    description:
      "Create purchase orders, track deliveries from suppliers, manage returns, and analyse your cost of goods.",
  },
];
// The owner reports that each merchant approved the exact quote and attribution
// below for public use. See docs/featured-merchant-permissions.md for scope.
const LIVE_STORES = [
  {
    name: "Pick More Stores",
    category: "Lingerie, Intimates & Sleepwear Boutique",
    quote: "In lingerie retail, managing delicate sizing combinations like 34B vs. 36C across multiple colors is a nightmare on basic spreadsheets. RetailPulsePOS handles our multi-variant catalog flawlessly. It locks stock across our counter and online shop in real time, so we never disappoint a customer by taking an online order for an out-of-stock size!",
  },
  {
    name: "Rtwbyquinsheba",
    category: "READY TO WEAR",
    quote: "Before RetailPulsePOS, my biggest fear was double-selling dresses online that were already bought in-store. Now, as soon as my cashier scans an item at the counter, our web store updates instantly in real time. It eliminated stock errors completely!",
  },
  {
    name: "Pinkyhome of Fashion",
    category: "Womenswear & Accessories",
    quote: "Managing sizes and color variants used to be a complete mess on spreadsheets. RetailPulsePOS tracks every single variant cleanly, and getting void alerts straight to my phone gives me total peace of mind when I’m away from the shop.",
  },
  {
    name: "Wowkiddies_n More Store",
    category: "Kids Wear & Toys",
    quote: "Our checkout queue moves twice as fast now during busy hours! The cashier interface is super fast, and our shoppers love receiving instant receipts on WhatsApp. It made our boutique look 10x more professional.",
  },
  {
    name: "TinyTrendyKiddies",
    category: "Children's Apparel & Shoes",
    quote: "Getting a free, auto-synced e-commerce catalog included with our POS subscription was the best decision for us. Our Instagram followers can browse live stock and pay via Paystack without us manually updating inventory every night.",
  },
  {
    name: "Sawpro Fittings",
    category: "Hardware, Appliances & Fittings",
    quote: "In a fittings store with hundreds of small items, tracking stock leaks is tough. RetailPulsePOS tightened our counter security completely—no missing receipts, zero cashier variance, and accurate stock counts every day.",
  },
  {
    name: "WING Frozen Foods",
    category: "Food & Cold Storage",
    quote: "Speed and inventory control are everything in frozen foods. RetailPulsePOS allows us to scan fast, record every cash and transfer payment cleanly, and monitor our total daily sales live from my mobile phone.",
  },
];
const FAQS = [
  {
    q: "Do I need a credit card to start the free trial?",
    a: "No. Your 10-day free trial starts the moment you sign up — no card required. You'll only be asked for payment details when you choose a plan after the trial.",
  },
  {
    q: "Can I use RetailPulse on my phone?",
    a: "Yes. RetailPulse is fully responsive and works on smartphones, tablets, and desktops. Your staff can use it at the till on any device.",
  },
  {
    q: "How is payment processed?",
    a: "All payments are handled securely by Paystack, Nigeria's leading payment gateway. You can pay with your debit card or via bank transfer.",
  },
  {
    q: "Can I add multiple staff with different access levels?",
    a: "Yes. You can create admin, manager, and staff accounts. Each level has appropriate access — staff can process sales, managers can view reports, and admins control everything.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "Your paid access continues until the end of your current paid period. From the date cancellation takes effect, you have 30 days to request and receive an export of your business data from support@retailpulsepos.com. After those 30 days, we delete your account and business data, except records we must keep by law. See our Privacy Policy for details.",
  },
  {
    q: "Is there a limit on the number of sales transactions?",
    a: "No — all plans include unlimited sales transactions. The product limits only apply to the number of distinct products in your catalogue.",
  },
];

const FOOTER_LINKS = {
  Product: [
    { label: "Features", action: "features" },
    { label: "Pricing", action: "pricing" },
    { label: "Demo", action: "demo" },
    { label: "Changelog", href: "/changelog" },
    { label: "Roadmap", href: "/roadmap" },
  ],
  Company: [
    { label: "About Us", action: "about" },
    { label: "Blog", action: "blog" },
    { label: "Careers", href: "/careers" },
  ],
  Support: [
    { label: "Help Centre", href: "/help" },
    { label: "Contact Us", href: "/contact" },
    { label: "WhatsApp Us", href: "https://wa.me/2349040460813", external: true },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Cookie Policy", href: "/cookie-policy" },
    { label: "Refund Policy", href: "/refund-policy" },
    { label: "Security", href: "/privacy-policy#8" },
  ],
};


// ─── Small helpers ─────────────────────────────────────────────────────────

function TruckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
    </svg>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full flex items-center justify-between py-5 text-left gap-4"
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium text-base">{q}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        )}
      </button>
      {open && (
        <p className="text-muted-foreground text-sm leading-relaxed pb-5 pr-8">{a}</p>
      )}
    </div>
  );
}

// ─── Demo Section ──────────────────────────────────────────────────────────

const DEMO_VIDEOS = [
  { label: "Overview", src: "demo.mp4" },
  { label: "Full Walkthrough", src: "demo2.mp4" },
];

function DemoSection({ base }: { base: string }) {
  const [active, setActive] = useState(0);
  const playedVideos = useRef(new Set<string>());
  return (
    <section id="demo" className="py-24 bg-muted/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-4xl font-bold tracking-tight mb-4">See RetailPulse in action</h2>
        <p className="text-muted-foreground text-lg mb-6">
          Watch how easy it is to manage your store, process sales, and track your finances.
        </p>
        <div className="flex justify-center gap-2 mb-6">
          {DEMO_VIDEOS.map((v, i) => (
            <button
              key={v.src}
              onClick={() => {
                trackEvent("homepage_action_clicked", { action: "demo_video_selected", location: "demo", video: v.label });
                setActive(i);
              }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                active === i
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:border-primary/50"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl bg-black aspect-video">
          <video
            key={DEMO_VIDEOS[active].src}
            className="w-full h-full object-contain"
            controls
            playsInline
            preload="metadata"
            onPlay={() => {
              const video = DEMO_VIDEOS[active];
              if (playedVideos.current.has(video.src)) return;
              playedVideos.current.add(video.src);
              trackEvent("homepage_demo_played", { video: video.label });
            }}
          >
            <source src={`${base}/${DEMO_VIDEOS[active].src}`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </section>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function Landing() {
  useEffect(() => {
    captureLandingCampaign(window.location.search);
  }, []);
  const scrollTo = (id: string, location: string) => {
    trackEvent("homepage_action_clicked", { action: id, location });
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  const trackAction = (action: string, location: string, plan?: string) =>
    trackHomepageAction(action, location, plan);
  const trackContact = (channel: string, location: string) =>
    trackEvent("homepage_contact_clicked", { channel, location });

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ══ Sticky Nav ══════════════════════════════════════════════ */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link href={`${BASE}/`} className="flex items-center gap-2 font-bold text-base sm:text-xl shrink-0">
            <img src={LOGO} alt="RetailPulsePOS" className="w-8 h-8 rounded-md object-cover" />
            <span>RetailPulsePOS</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
            <button onClick={() => scrollTo("features", "navigation")} className="hover:text-foreground transition-colors">Features</button>
            <button onClick={() => scrollTo("pricing", "navigation")} className="hover:text-foreground transition-colors">Pricing</button>
            <button onClick={() => scrollTo("live-stores", "navigation")} className="hover:text-foreground transition-colors">Live stores</button>
            <button onClick={() => scrollTo("faq", "navigation")} className="hover:text-foreground transition-colors">FAQ</button>
            <button onClick={() => scrollTo("demo", "navigation")} className="hover:text-foreground transition-colors">Demo</button>
          </nav>

          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <Link href={`${BASE}/login`} onClick={() => trackAction("sign_in", "navigation")}>
              <Button
                variant="outline"
                size="sm"
                className="border-primary/70 bg-background px-3 font-semibold text-foreground shadow-sm hover:bg-primary hover:text-primary-foreground sm:px-4"
              >
                Sign In
              </Button>
            </Link>
            <Link href={`${BASE}/signup`} onClick={() => trackAction("start_trial", "navigation")}>
              <Button size="sm" className="gap-1 px-2.5 sm:gap-1.5 sm:px-3">
                <span className="sm:hidden">Start Trial</span>
                <span className="hidden sm:inline">Start 10-Day Free Trial</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ══ Hero ════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-[#071B2E] text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B3150] via-[#071B2E] to-[#061321] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#38BDF8]/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-12 text-center">
          <Badge className="mb-6 rounded-full border border-[#38BDF8]/30 bg-[#38BDF8]/10 px-4 py-1.5 text-sm font-medium text-[#BAE6FD] shadow-none">
            Built for retail teams of every size
          </Badge>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-6 text-white">
            Smart POS &amp; Inventory Software<br className="hidden sm:block" /> for{" "}
            <span className="text-[#38BDF8]">Retail at Every Scale</span>
          </h1>

          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            Manage sales, inventory, staff, and performance across your locations from one connected platform.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link href={`${BASE}/signup`} onClick={() => trackAction("start_trial", "hero")}>
              <Button size="lg" className="h-12 px-8 text-base gap-2">
                Start 10-Day Free Trial <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="h-12 px-8 text-base border-white/30 text-white hover:bg-white/10" onClick={() => {
              trackAction("watch_demo", "hero");
              document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" });
            }}>
              Watch Demo
            </Button>
          </div>

          {/* Hero banner image */}
          <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl mb-14">
            <img
              src={heroImg}
              alt="Illustration of a retailer at the checkout"
              className="w-full h-auto object-cover"
              loading="eager"
            />
            <span className="absolute bottom-3 right-3 rounded-full bg-slate-950/75 px-3 py-1 text-xs text-white">Illustrative image</span>
          </div>

          <div className="max-w-2xl mx-auto rounded-xl border border-white/20 bg-white/5 px-6 py-5">
            <p className="font-semibold text-white">Stores using RetailPulsePOS</p>
            <p className="mt-1 text-sm text-slate-300">Hear from retailers who have shared their experiences with us.</p>
            <button onClick={() => scrollTo("live-stores", "hero")} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#7DD3FC] hover:text-white">
              View live stores <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ══ Features ════════════════════════════════════════════════ */}
      <section id="features" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4">Everything you need to run your store</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From the till to the back office — RetailPulse covers every part of your retail operation.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(f => (
              <div key={f.title} className="bg-background rounded-xl p-6 border border-border hover:shadow-md transition-shadow">
                <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>

          {/* Feature highlight image grid */}
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { src: featureCloudImg, alt: "Business owner reviewing secure cloud dashboard with analytics", caption: "Secure Cloud Dashboard" },
              { src: featureReceiptImg, alt: "Cashier handing customer a receipt — smart receipts that boost sales", caption: "Smart Receipt Management" },
              { src: featureMobileImg, alt: "Entrepreneur using smartphone to manage store POS dashboard anywhere", caption: "Manage From Anywhere" },
              { src: featureJoinImg, alt: "Confident African retail business owner — join the future of retail", caption: "Join the Future of Retail" },
            ].map(img => (
              <div key={img.caption} className="group relative rounded-xl overflow-hidden shadow-md aspect-square">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <p className="absolute bottom-3 left-3 right-3 text-white text-sm font-semibold leading-snug">{img.caption}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ About Us ════════════════════════════════════════════════ */}
      <section id="about" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl bg-[#071B2E] text-white p-8 sm:p-10 min-h-[360px] flex flex-col justify-between shadow-xl">
              <div className="w-14 h-14 rounded-xl bg-sky-400/15 flex items-center justify-center"><Store className="w-7 h-7 text-sky-300" /></div>
              <div>
                <p className="text-sky-300 text-sm font-semibold uppercase tracking-wider mb-3">Made for everyday retail</p>
                <p className="text-3xl font-bold leading-tight">Your products, sales and stock in one place.</p>
                <p className="text-slate-300 mt-4">Run your store in Naira, with tools for the payments and workflows you already use.</p>
              </div>
            </div>
            <div>
              <Badge className="mb-4">About Us</Badge>
              <h2 className="text-4xl font-bold tracking-tight mb-6">Built for Nigerian retailers</h2>
              <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                RetailPulsePOS brings sales, inventory and reporting together, with prices in Naira
                and practical workflows for cash, card and transfer payments.
              </p>
              <button onClick={() => scrollTo("live-stores", "about")} className="inline-flex items-center gap-2 font-medium text-primary hover:underline">
                See stores using RetailPulsePOS <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ Why RetailPulse ═════════════════════════════════════════ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold tracking-tight mb-6">
                Built for how Nigerian retail actually works
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                RetailPulse speaks your language — all prices in ₦, built-in support for cash, card, and transfer
                payments, and reports designed around the metrics that matter to your business.
              </p>
              <ul className="space-y-4">
                {[
                  { icon: TrendingUp, text: "Real-time P&L so you always know your margins" },
                  { icon: Shield, text: "Role-based access — admin, manager, and staff levels" },
                  { icon: Clock, text: "Staff clock-in/out with attendance tracking" },
                  { icon: RefreshCcw, text: "Sales returns and refund management built-in" },
                  { icon: Store, text: "Multi-store ready with fully isolated store data" },
                  { icon: Star, text: "Loyalty points and per-customer discount management" },
                ].map(item => (
                  <li key={item.text} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-3.5 h-3.5 text-emerald-600" />
                    </div>
                    <span className="text-muted-foreground">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Illustrative dashboard — not actual merchant data */}
            <div className="bg-muted/50 rounded-2xl p-8 border border-border">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-4">Illustrative dashboard · sample figures</p>
              <div className="space-y-4">
                <div className="bg-background rounded-lg p-4 border border-border">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-muted-foreground">Today's Revenue</span>
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="text-2xl font-bold">₦284,500</div>
                  <div className="text-xs text-emerald-600 mt-1">↑ 12% from yesterday</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <div className="text-xs text-muted-foreground mb-1">Transactions</div>
                    <div className="text-xl font-bold">47</div>
                  </div>
                  <div className="bg-background rounded-lg p-4 border border-border">
                    <div className="text-xs text-muted-foreground mb-1">Low Stock Items</div>
                    <div className="text-xl font-bold text-amber-600">3</div>
                  </div>
                </div>
                <div className="bg-background rounded-lg p-4 border border-border">
                  <div className="text-xs text-muted-foreground mb-2">Top Products Today</div>
                  {[
                    ["Indomie Noodles", "x140", "₦56,000"],
                    ["Zobo Drink 50cl", "x96", "₦28,800"],
                    ["Semovita 2kg", "x73", "₦43,800"],
                  ].map(([name, qty, rev]) => (
                    <div key={name} className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0">
                      <span className="text-sm">{name}</span>
                      <span className="text-xs text-muted-foreground">{qty}</span>
                      <span className="text-xs font-medium">{rev}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ Pricing ═════════════════════════════════════════════════ */}
      <section id="pricing" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4">Simple, transparent pricing</h2>
            <p className="text-muted-foreground text-lg">
              All plans start with a 10-day free trial. No credit card required to begin.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PLANS.map(plan => (
              <div
                key={plan.name}
                className={`relative rounded-2xl border p-8 flex flex-col ${
                  plan.highlight
                    ? "border-primary bg-primary text-primary-foreground shadow-xl scale-105"
                    : plan.enterprise
                    ? "border-amber-400 bg-background shadow-lg shadow-amber-100"
                    : "border-border bg-background"
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-700 text-white border-0 px-4 py-1 text-sm shadow">
                      Most Popular
                    </Badge>
                  </div>
                )}
                {plan.enterprise && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-700 text-white border-0 px-4 py-1 text-sm shadow gap-1">
                      <Crown className="w-3 h-3" /> Enterprise
                    </Badge>
                  </div>
                )}
                <div className="mb-6">
                  <h3 className={`text-xl font-bold mb-1 ${plan.highlight ? "text-primary-foreground" : ""}`}>{plan.name}</h3>
                  <p className={`text-sm mb-4 ${plan.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{plan.description}</p>
                  <div className="flex items-end gap-1">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className={`text-sm mb-1.5 ${plan.highlight ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 flex-1 mb-8">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.highlight ? "text-primary-foreground/80" : plan.enterprise ? "text-amber-500" : "text-emerald-500"}`} />
                      <span className={plan.highlight ? "text-primary-foreground/90" : ""}>{f}</span>
                    </li>
                  ))}
                </ul>
                {plan.enterprise ? (
                  <div className="flex flex-col gap-2">
                    <Link href={`${BASE}/signup`} onClick={() => trackAction("start_trial", "pricing", plan.name)}>
                      <Button className="w-full bg-amber-700 hover:bg-amber-800 text-white" size="lg">
                        <Crown className="w-4 h-4 mr-1.5" /> Subscribe Now
                      </Button>
                    </Link>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackContact("whatsapp", "pricing")}>
                      <Button variant="outline" className="w-full gap-2 border-amber-400 text-amber-700 hover:bg-amber-50" size="lg">
                        <MessageCircle className="w-4 h-4" /> Contact Sales
                      </Button>
                    </a>
                  </div>
                ) : (
                  <Link href={`${BASE}/signup`} onClick={() => trackAction("start_trial", "pricing", plan.name)}>
                    <Button
                      className={`w-full ${plan.highlight ? "bg-white text-primary hover:bg-white/90" : ""}`}
                      variant={plan.highlight ? "secondary" : "default"}
                      size="lg"
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Pricing footnote */}
          <p className="text-center text-sm text-muted-foreground mt-10">
            All prices are in Nigerian Naira (₦) and billed monthly. Annual billing available — save up to 20%.
            Payments processed securely by <span className="font-medium text-foreground">Paystack</span>.
          </p>
        </div>
      </section>

      {/* ══ Coming Soon ═════════════════════════════════════════════ */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-indigo-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <Badge className="mb-5 bg-amber-500/20 text-amber-300 border-amber-500/30 hover:bg-amber-500/20">
            🚀 Coming Soon
          </Badge>
          <h2 className="text-4xl font-bold tracking-tight text-white mb-4">
            Keep watching — more is on the way
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-12">
            We're building powerful new modules to complete your entire retail ecosystem. Subscribe to be the first to know when they launch.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Logistics */}
            <div className="relative bg-white/5 border border-white/10 rounded-2xl p-8 text-left group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent pointer-events-none" />
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-bold text-white">Logistics</h3>
                <span className="text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                End-to-end delivery management built right into RetailPulse. Track shipments, manage dispatch riders, coordinate deliveries, and give customers real-time order updates — all from one dashboard.
              </p>
              <ul className="mt-4 space-y-1.5">
                {["Order dispatch & tracking", "Rider management", "Delivery zones & pricing", "Customer delivery notifications"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-slate-400">
                    <div className="w-1 h-1 rounded-full bg-blue-400 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            {/* Ecommerce */}
            <div className="relative bg-white/5 border border-white/10 rounded-2xl p-8 text-left group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent pointer-events-none" />
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-bold text-white">E-commerce</h3>
                <span className="text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded-full font-medium">Coming Soon</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Your own online store powered by your RetailPulse inventory — no extra setup needed. Sell to customers anywhere in Nigeria with automatic stock sync between your physical and online channels.
              </p>
              <ul className="mt-4 space-y-1.5">
                {["Online storefront for your products", "Inventory synced with your POS", "Online payment with Paystack", "Order management from your dashboard"].map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-slate-400">
                    <div className="w-1 h-1 rounded-full bg-emerald-400 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-10 text-slate-500 text-sm">
            Already subscribed? You'll get early access when these features launch.{" "}
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline font-medium" onClick={() => trackContact("whatsapp", "waitlist")}>
              Join the waitlist on WhatsApp →
            </a>
          </p>
        </div>
      </section>

      {/* ══ Merchant-approved stories ══════════════════════════════════ */}
      <section id="live-stores" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Merchant stories</Badge>
            <h2 className="text-4xl font-bold tracking-tight mb-4">Hear from stores using RetailPulsePOS</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              In their own words, these retailers share how they use RetailPulsePOS.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {LIVE_STORES.map(store => (
              <figure key={store.name} className="flex flex-col justify-between rounded-xl border border-border bg-background px-6 py-6 shadow-sm">
                <blockquote className="text-foreground leading-relaxed">“{store.quote}”</blockquote>
                <figcaption className="mt-6 flex items-center gap-3 border-t border-border pt-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10" aria-hidden="true">
                    <Store className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold leading-snug break-words">{store.name}</p>
                    <p className="text-sm text-muted-foreground">{store.category}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            Merchant-reported experiences; individual results have not been independently verified. Feature availability varies; e-commerce is not yet generally available.
          </p>
        </div>
      </section>

      {/* ══ Demo ════════════════════════════════════════════════════ */}
      <DemoSection base={BASE} />

      {/* ══ FAQ ═════════════════════════════════════════════════════ */}
      <section id="faq" className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4">Frequently asked questions</h2>
            <p className="text-muted-foreground text-lg">
              Everything you need to know before getting started.
            </p>
          </div>
          <div className="bg-background border border-border rounded-2xl px-8">
            {FAQS.map(faq => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
          <p className="text-center text-muted-foreground text-sm mt-8">
            Still have questions?{" "}
            <a href="mailto:support@retailpulsepos.com" className="text-primary font-medium hover:underline" onClick={() => trackContact("email", "faq")}>
              Email our support team
            </a>
            {" "}or{" "}
            <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer" className="text-green-600 font-medium hover:underline" onClick={() => trackContact("whatsapp", "faq")}>
              chat on WhatsApp
            </a>
          </p>
        </div>
      </section>

      {/* ══ Blog / Updates ══════════════════════════════════════════ */}
      <section id="blog" className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4">Retail Tips & Updates</Badge>
            <h2 className="text-4xl font-bold tracking-tight mb-4">From the RetailPulse blog</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Practical guides, seasonal strategies, and product news to help you run a smarter store.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Photo blog cards */}
            {[
              {
                img: holidayImg1,
                date: "November 2024",
                tag: "Seasonal Strategy",
                title: "How to prepare your POS for the Christmas rush",
                excerpt: "Stock up, staff up, and set up automatic reorder alerts before the festive season hits. Here's your 5-step holiday readiness checklist.",
              },
              {
                img: holidayImg2,
                date: "December 2024",
                tag: "Business Growth",
                title: "Turn festive foot traffic into repeat customers",
                excerpt: "The holidays bring new shoppers to your store. Learn how to use RetailPulse's loyalty features to convert them into year-round regulars.",
              },
            ].map(post => (
              <div key={post.title} className="bg-background rounded-2xl border border-border overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.img}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex flex-col gap-3 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">{post.tag}</Badge>
                    <span className="text-xs text-muted-foreground">{post.date}</span>
                  </div>
                  <h3 className="font-semibold text-lg leading-snug">{post.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed flex-1">{post.excerpt}</p>
                  <span className="text-primary text-sm font-medium inline-flex items-center gap-1">
                    Coming soon <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            ))}

            {/* Help centre */}
            <div className="bg-primary rounded-2xl p-8 flex flex-col justify-between text-primary-foreground">
              <div>
                <Badge className="mb-4 bg-white/20 text-white border-0 hover:bg-white/30">Help Centre</Badge>
                <h3 className="text-2xl font-bold mb-3 leading-snug">See how to get started</h3>
                <p className="text-primary-foreground/80 text-sm leading-relaxed mb-6">
                  Follow step-by-step guides for setting up your store, adding products, and recording a sale.
                </p>
              </div>
              <Link href={`${BASE}/help`} onClick={() => trackAction("open_help", "help_card")}>
                <Button className="w-full bg-white text-primary hover:bg-white/90" size="lg">
                  Explore the Help Centre <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA Banner ══════════════════════════════════════════════ */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-4xl font-bold tracking-tight mb-4">Ready to grow your retail business?</h2>
          <p className="text-primary-foreground/80 text-lg mb-8">
            Join independent Nigerian retailers already using RetailPulsePOS.
            Start your free 10-day trial today — no credit card needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href={`${BASE}/signup`} onClick={() => trackAction("start_trial", "bottom_banner")}>
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 h-12 px-8 text-base gap-2">
                Get Started Free <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link href={`${BASE}/login`} onClick={() => trackAction("sign_in", "bottom_banner")}>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8 text-base">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ══ Footer ══════════════════════════════════════════════════ */}
      <footer className="bg-background pt-16 pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Top: brand + columns */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-10 pb-12 border-b border-border">

            {/* Brand column */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 font-bold text-xl mb-4">
                <img src={LOGO} alt="RetailPulsePOS" className="w-8 h-8 rounded-md object-cover" />
                RetailPulsePOS
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                Providing intelligent retail solutions for modern Nigerian retailers. Sell, track, manage, and grow —
                all from one dashboard. From ₦7,500/month.
              </p>

              {/* Contact info */}
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-primary" />
                  <a href="mailto:info@retailpulsepos.com" className="hover:text-foreground transition-colors" onClick={() => trackContact("email", "footer_info")}>info@retailpulsepos.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-primary" />
                  <a href="mailto:sales@retailpulsepos.com" className="hover:text-foreground transition-colors" onClick={() => trackContact("email", "footer_sales")}>sales@retailpulsepos.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-primary" />
                  <a href="mailto:support@retailpulsepos.com" className="hover:text-foreground transition-colors" onClick={() => trackContact("email", "footer_support")}>support@retailpulsepos.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-primary" />
                  <a href="tel:+2349040460813" className="hover:text-foreground transition-colors" onClick={() => trackContact("phone", "footer")}>+234 904 046 0813</a>
                </li>
              </ul>

              {/* Social icons */}
              <div className="flex items-center gap-3">
                {[
                  { icon: Twitter, label: "Twitter / X", href: "https://twitter.com/retailpulsepos" },
                  { icon: Facebook, label: "Facebook", href: "https://facebook.com/retailpulsepos" },
                  { icon: Instagram, label: "Instagram", href: "https://instagram.com/retailpulsepos" },
                  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com/company/retailpulsepos" },
                  { icon: MessageCircle, label: "WhatsApp", href: "https://wa.me/2349040460813" },
                ].map(s => (
                  <a
                    key={s.label}
                    href={s.href}
                    onClick={s.label === "WhatsApp" ? () => trackContact("whatsapp", "footer_social") : undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors text-muted-foreground"
                  >
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>

            {/* Link columns */}
            {Object.entries(FOOTER_LINKS).map(([heading, links]) => (
              <div key={heading}>
                <h4 className="font-semibold text-sm mb-4">{heading}</h4>
                <ul className="space-y-2.5">
                  {links.map(link => (
                    <li key={link.label}>
                      {"action" in link ? (
                        <button
                          onClick={() => scrollTo(link.action!, "footer")}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {link.label}
                        </button>
                      ) : (
                        <a
                          href={link.href}
                          onClick={() => link.label === "WhatsApp Us"
                            ? trackContact("whatsapp", "footer_links")
                            : link.label === "Contact Us"
                              ? trackAction("contact", "footer")
                              : undefined}
                          {...("external" in link && link.external
                            ? { target: "_blank", rel: "noopener noreferrer" }
                            : {})}
                          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 RetailPulse Technologies Ltd. All rights reserved. RC 9583877. Made with ❤️ in Nigeria 🇳🇬
            </p>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground">Payments secured by</span>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-md">
                <Shield className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-xs font-semibold">Paystack</span>
              </div>
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-md">
                <Shield className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-semibold">SSL Secured</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
